import React, { useState, useEffect, useCallback, useRef } from "react";
import { Routes, Route, useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-hot-toast";
import { FormTemplate, FormFieldDefinition, FormSection } from "../../db/schema";
import { resolveFormPdfUrl } from "../../services/pdfService";
import FormTemplateManager from "./FormTemplateManager";
import FormTemplateUpload from "./FormTemplateUpload";
import FormDesigner from "./FormDesigner";
import { pickDroppedPdf } from "./pickDroppedPdf";
import { goBackOr } from "../../utilities/goBack";
import TemplateProcessingNotice from "./TemplateProcessingNotice";
import { hasPendingSuggestions, isTemplateProcessing } from "../../utilities/formTemplates";
import { FORMS_CHANGED_EVENT } from "../../services/formUploadService";
import FormFiller from "./FormFiller";
import AudioPlayback from "../Menu/AudioPlayback";
import UploadModal from "../Menu/UploadModal";
import { useAuth } from "../../context/AuthContext";
import { followFormSupplement, processFormFill } from "../../services/formAudioProcessingService";

/**
 * Top-level router for /forms/* routes.
 * Fetches templates once and passes them down to child routes.
 */
const FormsRouter: React.FC = () => {
  const [templates, setTemplates] = useState<FormTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  // Mounted outside <Routes> so switching between /forms and /forms/upload
  // never unmounts the open dialog.
  const uploadModalRef = useRef<HTMLDialogElement>(
    null
  ) as React.RefObject<HTMLDialogElement>;
  const openUpload = useCallback(
    () => uploadModalRef.current?.showModal(),
    []
  );
  // A PDF dropped on the list: open the modal with it loaded.
  const [droppedFile, setDroppedFile] = useState<File | null>(null);
  const handleFilesDropped = useCallback((files: File[]) => {
    const pdf = pickDroppedPdf(files);
    if (!pdf) return;
    setDroppedFile(pdf);
    uploadModalRef.current?.showModal();
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      const response = await axios.get("/api/forms/templates");
      setTemplates(response.data || []);
    } catch (error) {
      console.error("Error fetching form templates:", error);
      toast.error("Failed to fetch form templates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // A form being read by the AI is locked until it finishes (possibly in another
  // tab, or for another officer, with an agency form). Keep looking until done.
  const anyProcessing = templates.some(isTemplateProcessing);
  useEffect(() => {
    if (!anyProcessing) return;
    const timer = setInterval(fetchTemplates, 5000);
    return () => clearInterval(timer);
  }, [anyProcessing, fetchTemplates]);

  // Uploads finish in the background (tray); show them in the list as they land.
  useEffect(() => {
    window.addEventListener(FORMS_CHANGED_EVENT, fetchTemplates);
    return () => window.removeEventListener(FORMS_CHANGED_EVENT, fetchTemplates);
  }, [fetchTemplates]);

  return (
    <>
      <Routes>
        <Route
          index
          element={
            <FormTemplateManager
              templates={templates}
              loading={loading}
              onRefresh={fetchTemplates}
              onUploadClick={openUpload}
              onFilesDropped={handleFilesDropped}
            />
          }
        />
        <Route
          path="upload"
          element={
            <FormTemplateManager
              templates={templates}
              loading={loading}
              onRefresh={fetchTemplates}
              onUploadClick={openUpload}
              onFilesDropped={handleFilesDropped}
              autoOpenUpload
            />
          }
        />
        <Route
          path=":templateId"
          element={<TemplateDetailRoute templates={templates} onRefresh={fetchTemplates} />}
        />
        <Route
          path=":templateId/edit"
          element={<EditRoute templates={templates} onRefresh={fetchTemplates} />}
        />
        <Route
          path=":templateId/submissions/:submissionId"
          element={<SubmissionRoute templates={templates} />}
        />
      </Routes>
      <FormTemplateUpload modalRef={uploadModalRef} droppedFile={droppedFile} />
    </>
  );
};

// --- Route wrapper components ---

function TemplateDetailRoute({
  templates,
  onRefresh,
}: {
  templates: FormTemplate[];
  onRefresh: () => Promise<void>;
}) {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const template = templates.find((t) => t.id === templateId);

  if (templates.length === 0) {
    return (
      <div className="flex-1 overflow-auto p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-base-300 rounded w-1/4"></div>
          <div className="h-32 bg-base-300 rounded"></div>
        </div>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="flex-1 overflow-auto p-6">
        <p className="text-base-content/70">Template not found.</p>
        <button
          onClick={() => navigate("/forms")}
          className="text-sm text-primary hover:underline mt-2"
        >
          &larr; Back to templates
        </button>
      </div>
    );
  }

  if (isTemplateProcessing(template)) {
    return <TemplateProcessingNotice name={template.name} backPath="/forms" templateId={template.id} />;
  }

  return (
    <FormTemplateManager
      templates={templates}
      loading={false}
      onRefresh={onRefresh}
      viewTemplate={template}
    />
  );
}

function EditRoute({
  templates,
  onRefresh,
}: {
  templates: FormTemplate[];
  onRefresh: () => Promise<void>;
}) {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState<FormTemplate | undefined>(
    templates.find((t) => t.id === templateId)
  );

  // Update when templates load/change
  useEffect(() => {
    const found = templates.find((t) => t.id === templateId);
    if (found) setTemplate(found);
  }, [templates, templateId]);

  // While the AI works on this form the designer stays on screen, locked. When
  // that ends it is replaced by a fresh one (see `key` below).
  const locked = !!template && isTemplateProcessing(template);
  const [lockEpoch, setLockEpoch] = useState(0);
  const wasLocked = useRef(locked);
  useEffect(() => {
    if (wasLocked.current && !locked) setLockEpoch((n) => n + 1);
    wasLocked.current = locked;
  }, [locked]);

  if (!template) {
    return (
      <div className="flex-1 overflow-auto p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-base-300 rounded w-1/4"></div>
          <div className="h-32 bg-base-300 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <FormDesigner
      // New instance when a lock ends, so the designer starts from the finished
      // fields (and opens any suggestions that are waiting).
      key={`${template.id}-${lockEpoch}`}
      lockedByServer={locked}
      backPath="/forms"
      template={template}
      initialFields={template.fields || []}
      initialSections={template.field_sections || []}
      pendingSuggestions={hasPendingSuggestions(template)}
      onSave={async (fields: FormFieldDefinition[], sections: FormSection[]) => {
        try {
          await axios.patch(`/api/forms/templates/${template.id}`, {
            fields,
            field_sections: sections,
          });
          toast.success("Template saved successfully");
          setTemplate({ ...template, fields, field_sections: sections });
          await onRefresh();
        } catch (error) {
          console.error("Error saving template:", error);
          toast.error("Failed to save template");
        }
      }}
      onCancel={() => navigate(`/forms/${templateId}`)}
    />
  );
}

function SubmissionRoute({ templates }: { templates: FormTemplate[] }) {
  const { templateId, submissionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const template = templates.find((t) => t.id === templateId);
  const [submission, setSubmission] = useState<any>(null);
  const [loadingSubmission, setLoadingSubmission] = useState(true);
  // Adding more audio to this filled form: uploading or being filled.
  const [addingMore, setAddingMore] = useState(false);
  // Bumped when a fill lands, so the editor restarts from the new values.
  const [valuesEpoch, setValuesEpoch] = useState(0);
  const [pdfUrl, setPdfUrl] = useState("");
  const [hasEvidenceIntegration, setHasEvidenceIntegration] = useState(false);
  const recordModalRef = useRef<HTMLDialogElement>(null) as React.RefObject<HTMLDialogElement>;
  const uploadModalRef = useRef<HTMLDialogElement>(null) as React.RefObject<HTMLDialogElement>;

  const fetchSubmission = useCallback(async () => {
    try {
      const { data } = await axios.get(`/api/forms/submissions/${submissionId}`);
      setSubmission(data?.submission ?? null);
      return data?.submission ?? null;
    } catch (error) {
      console.error("Error fetching submission:", error);
      return null;
    } finally {
      setLoadingSubmission(false);
    }
  }, [submissionId]);

  const afterAddition = useCallback(async () => {
    await fetchSubmission();
    setValuesEpoch((n) => n + 1);
    setAddingMore(false);
  }, [fetchSubmission]);

  useEffect(() => {
    fetchSubmission().then((s) => {
      // Opened while an addition is still filling: wait for it here.
      if (s?.status === "processing" && s?.input_metadata?.supplement) {
        setAddingMore(true);
        followFormSupplement(s.id, {
          onSuccess: () => void afterAddition(),
          onError: (err) => {
            toast.error(err);
            void afterAddition();
          },
        });
      }
    });
  }, [fetchSubmission, afterAddition]);

  useEffect(() => {
    let cancelled = false;
    if (template?.original_pdf_url) {
      resolveFormPdfUrl(template.original_pdf_url).then((u) => !cancelled && setPdfUrl(u));
    }
    return () => {
      cancelled = true;
    };
  }, [template?.original_pdf_url]);

  useEffect(() => {
    if (!user?.agency_id) return;
    axios
      .get("/api/settings/evidence-integration")
      .then((res) => setHasEvidenceIntegration(!!res.data.integration))
      .catch(() => setHasEvidenceIntegration(false));
  }, [user?.agency_id]);

  // Same upload path as the first fill, pointed at this submission.
  const addFiles = (fileConfigs: any[]) => {
    if (!submission || !template) return;
    setAddingMore(true);
    void processFormFill(fileConfigs, {
      user,
      templateId: template.id,
      submissionId: submission.id,
      onSuccess: () => void afterAddition(),
      onError: () => void afterAddition(),
    });
  };

  // The recorder shows the form beside it. Adding to a filled form, that is the
  // form as it is now, answers written in (edits were saved just before), so
  // the officer can see what is already there. Falls back to the blank form.
  const openRecorder = async () => {
    if (submission) {
      const toastId = toast.loading("Loading the form...");
      try {
        const { data } = await axios.post(`/api/forms/submissions/${submission.id}/export-pdf`);
        if (data?.filled_pdf_url) setPdfUrl(await resolveFormPdfUrl(data.filled_pdf_url));
        toast.dismiss(toastId);
      } catch {
        toast.error("Couldn't show the filled form; showing the blank one", { id: toastId });
      }
    }
    recordModalRef.current?.showModal();
  };

  const cancelAddition = async () => {
    if (!submission) return;
    await axios.post(`/api/forms/submissions/${submission.id}/supplement/cancel`).catch(() => {});
    await afterAddition();
  };

  if (!template || loadingSubmission) {
    return (
      <div className="flex-1 overflow-auto p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-base-300 rounded w-1/4"></div>
          <div className="h-32 bg-base-300 rounded"></div>
        </div>
      </div>
    );
  }

  if (!submission) {
    return (
      <div className="flex-1 overflow-auto p-6">
        <p className="text-base-content/70">Submission not found.</p>
        <button
          onClick={() => goBackOr(navigate, location, `/forms/${templateId}`)}
          className="text-sm text-primary hover:underline mt-2"
        >
          &larr; Back
        </button>
      </div>
    );
  }

  return (
    <>
    <FormFiller
      key={`${submission.id}-${valuesEpoch}`}
      template={template}
      onAddMore={(how) =>
        how === "record" ? void openRecorder() : uploadModalRef.current?.showModal()
      }
      addingMore={addingMore}
      onCancelAddMore={cancelAddition}
      // Back goes to wherever the officer came from, not always the form's page.
      onCancel={() => goBackOr(navigate, location, `/forms/${templateId}`)}
      fieldValues={submission.field_values || {}}
      onSaveValues={async (editedValues) => {
        try {
          await axios.patch(`/api/forms/submissions/${submission.id}`, {
            field_values: editedValues,
          });
          toast.success("Values saved");
        } catch (err) {
          toast.error("Failed to save values");
        }
      }}
      onExportPdf={async (editedValues) => {
        try {
          toast.loading("Generating PDF...", { id: "export-pdf" });
          await axios.patch(`/api/forms/submissions/${submission.id}`, {
            field_values: editedValues,
          });
          const response = await axios.post(
            `/api/forms/submissions/${submission.id}/export-pdf`
          );
          const pdfUrl = response.data.filled_pdf_url;
          if (pdfUrl) {
            toast.success("PDF generated!", { id: "export-pdf" });
            const signedUrl = await resolveFormPdfUrl(pdfUrl);
            const blob = await fetch(signedUrl).then((r) => r.blob());
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${template?.name || "form"}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
          } else {
            toast.error("PDF generation failed", { id: "export-pdf" });
          }
        } catch (err) {
          toast.error("Failed to export PDF", { id: "export-pdf" });
        }
      }}
    />

    {/* The same recorder and upload modal as the first fill. */}
    <AudioPlayback
      modalRef={recordModalRef}
      handleAudioFileChange={async (blob: any, audioType?: string) => {
        const file = new File([blob as Blob], `recording-${Date.now()}.wav`, { type: "audio/wav" });
        addFiles([
          {
            file,
            audioType: (audioType || "narrative") as "narrative" | "interaction",
            speakers: [],
            additionalContext: "",
          },
        ]);
      }}
      pdfUrl={pdfUrl || undefined}
    />
    <UploadModal
      modalRef={uploadModalRef}
      onUpload={(files) => addFiles(files.map(({ mediaType, ...rest }: any) => rest))}
      hideReportTemplate
      hasEvidenceIntegration={hasEvidenceIntegration}
    />
    </>
  );
}

export default FormsRouter;
